import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ArrowRight, Loader2, Clock, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { RecipeImageWithFallback } from './figma/RecipeImageWithFallback';
import type { Product } from './ProductCard';
import { useShopifySearch, type Recipe } from '../hooks/useShopifySearch';
import { useShopifyCollections } from '../hooks/useShopifyProducts';

interface SearchResultsPopupProps {
  searchQuery: string;
  isVisible: boolean;
  onClose: () => void;
  onProductClick: (product: Product) => void;
  onRecipeClick: (recipe: Recipe) => void;
  onViewAllResults: (query: string) => void;
  onAddToCart: (product: Product) => void;
}

export function SearchResultsPopup({
  searchQuery,
  isVisible,
  onClose,
  onProductClick,
  onRecipeClick,
  onViewAllResults,
  onAddToCart: _onAddToCart,
}: SearchResultsPopupProps) {
  const { t } = useTranslation(['search']);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const popupRef = useRef<HTMLDivElement>(null);

  const { search, clearSearch, searchResults } = useShopifySearch();
  const { collections } = useShopifyCollections();

  // Auto-search when query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      clearSearch();
      return;
    }

    const timer = setTimeout(() => {
      search(searchQuery);
      setSelectedIndex(-1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, search, clearSearch]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!searchResults) return;

      const totalItems =
        searchResults.products.length + searchResults.recipes.length;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        if (selectedIndex < searchResults.products.length) {
          onProductClick(searchResults.products[selectedIndex]);
        } else {
          const recipeIndex = selectedIndex - searchResults.products.length;
          onRecipeClick(searchResults.recipes[recipeIndex]);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isVisible,
    selectedIndex,
    searchResults,
    searchQuery,
    onProductClick,
    onRecipeClick,
    onViewAllResults,
    onClose,
  ]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const { products, recipes, totalResults, isLoading } = searchResults;
  const showResults = searchQuery.trim() && !isLoading && totalResults > 0;
  const showEmptyState = searchQuery.trim() && !isLoading && totalResults === 0;
  const showSuggestions = !searchQuery.trim();

  // TODO: Handle search results display logic

  return (
    <div
      ref={popupRef}
      className="absolute top-full right-0 mt-2 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in-0 slide-in-from-top-2 duration-300"
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(25px)',
        WebkitBackdropFilter: 'blur(25px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow:
          '0 20px 64px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(245, 158, 11, 0.15)',
        maxHeight: '70vh',
        width: '400px',
      }}
    >
      {/* Neural gradient overlay */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none z-0"
        style={{
          background:
            'linear-gradient(135deg, rgba(245, 158, 11, 0.03) 0%, rgba(217, 119, 6, 0.01) 50%, rgba(180, 83, 9, 0.03) 100%)',
        }}
      />

      <div className="relative max-h-full overflow-y-auto scrollbar-hide z-10">
        {/* Loading State */}
        {isLoading && (
          <div className="p-6 flex items-center justify-center space-x-3">
            <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
            <span className="text-gray-600 font-medium">
              {t('search:popup.searching')}
            </span>
          </div>
        )}

        {/* Search Results */}
        {showResults && (
          <div className="p-6">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-orange-100">
                  <Search className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('search:popup.foundResults', { count: totalResults })}
                  </h3>
                  <p className="text-sm text-gray-500">{t('search:popup.for')} "{searchQuery}"</p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewAllResults(searchQuery);
                }}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-xl"
                style={{ position: 'relative', zIndex: 20 }}
              >
{t('search:popup.viewAll')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Products */}
            {products.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
{t('search:popup.products')} ({products.length})
                </h4>
                <div className="space-y-2">
                  {products.slice(0, 4).map((product, index) => (
                    <div
                      key={product.id}
                      className={`flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedIndex === index
                          ? 'bg-amber-50 ring-2 ring-amber-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        onProductClick(product);
                      }}
                    >
                      <div className="flex items-center space-x-4">
                        {/* Product Image */}
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <ImageWithFallback
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h5 className="font-medium text-gray-900 truncate">
                              {product.name}
                            </h5>
                            {product.tags.includes('bestseller') && (
                              <Badge
                                variant="secondary"
                                className="text-xs bg-amber-100 text-amber-800"
                              >
{t('search:popup.bestseller')}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <div className="flex items-center">
                              <Star className="w-3 h-3 text-yellow-400 fill-current mr-1" />
                              <span>{product.rating}</span>
                            </div>
                            <span>•</span>
                            <span className="font-semibold text-amber-600">
                              ${product.price}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recipes */}
            {recipes.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
{t('search:popup.recipes')} ({recipes.length})
                </h4>
                <div className="space-y-2">
                  {recipes.slice(0, 3).map((recipe, index) => (
                    <div
                      key={recipe.id}
                      className={`flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedIndex === products.length + index
                          ? 'bg-green-50 ring-2 ring-green-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => onRecipeClick(recipe)}
                    >
                      <div className="flex items-center space-x-4">
                        {/* Recipe Image */}
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <RecipeImageWithFallback
                            src={recipe.image?.url}
                            alt={recipe.title}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Recipe Info */}
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 truncate mb-1">
                            {recipe.title}
                          </h5>
                          <p className="text-sm text-gray-500 line-clamp-1">
                            {recipe.excerpt}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {showEmptyState && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('search:popup.noResults.title')}
            </h3>
            <p className="text-gray-500">
              {t('search:popup.noResults.subtitle')}
            </p>
          </div>
        )}

        {/* Search Suggestions */}
        {showSuggestions && (
          <div className="p-6">
            {/* Popular Categories */}
            {collections.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-500" />
{t('search:popup.suggestions.title')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {collections.slice(0, 6).map(collection => (
                    <Button
                      key={collection.id}
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewAllResults(collection.title)}
                      className="text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-full px-4 py-2 text-sm"
                    >
                      {collection.title}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
